/* HomeCrest AI studio - Design Your Own furniture visualiser.
 * ===========================================================================
 * WHAT THIS FILE IS
 * The complete front-end state machine for the left column of the Design Your
 * Own page: the customer's isolated product, an AI result, the comparison
 * between them, and the prompt controls that request a new result.
 *
 * THE BACKEND (staging)
 * The Cloudflare Worker `homecrest-preview` exposes two routes for this page.
 * Both are wired here; the base URL and the Turnstile sitekey are SECTION
 * SETTINGS rendered onto the studio element, never constants in this file, so
 * pointing the page at staging or production is a theme-editor change.
 *
 *   POST /isolate   multipart: image, session_id, generation_id,
 *                   turnstile_token
 *                -> { ok, image_url, object_key }   furniture alone on white
 *
 *   POST /edit      JSON: object_key, instruction, session_id, generation_id,
 *                   turnstile_token, context
 *                -> { ok, image_url, object_key }   the same piece, modified
 *
 * THE TWO RULES THAT SHAPE EVERYTHING BELOW
 *
 * 1. The room photo is raw material, not the Original. It is posted ONCE, to
 *    /isolate, and the isolated product that comes back becomes the Original.
 *    The raw upload is never shown in the viewer and is never sent to /edit.
 *
 * 2. The Original is never overwritten. Every /edit derives from the ORIGINAL
 *    object key, not from the previous result, so a second generation is a
 *    second interpretation of the same piece rather than a copy of a copy.
 *
 * WHAT NEVER LEAVES THE SERVER
 * No key, secret or bucket path is known to this file. The Worker returns a
 * short-lived signed /i/ URL for display and an opaque object id which is held
 * in memory only - never written to the DOM, the URL or storage.
 *
 * NO ENDPOINT CONFIGURED
 * The studio still works as a viewer: the chosen photo is previewed locally
 * and Generate says plainly that visualisation is not connected. A backend can
 * also be supplied from outside, which takes precedence over the Worker:
 *
 *   window.HomeCrestAIStudio.registerGenerator(async ({ prompt, original }) => {
 *     //  original = { file, url, objectKey, name, dataUrl }
 *     //  return   { url: string }   - anything an <img> can load
 *   });
 */

(() => {
  const STAGE_STATES = [
    'empty', 'detecting', 'selecting', 'isolating', 'ready', 'generating', 'generated', 'error', 'unavailable',
  ];

  /* The Worker's own validation. Mirrored here so a request that cannot
     possibly succeed is refused before it costs a round trip. */
  const ID_RE = /^[A-Za-z0-9_-]{8,64}$/;
  const MAX_INSTRUCTION_CHARS = 500;
  const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const TURNSTILE_TIMEOUT_MS = 45000;

  /* One registered generator for the page. Deliberately module-level: a second
     studio instance on the same page would share it rather than race. */
  let generator = null;

  const api = {
    registerGenerator(fn) {
      if (typeof fn !== 'function') throw new TypeError('registerGenerator expects a function');
      generator = fn;
      document.dispatchEvent(new CustomEvent('homecrest:ai-generator-ready'));
    },
    hasGenerator() {
      return typeof generator === 'function';
    },
  };
  window.HomeCrestAIStudio = Object.assign(window.HomeCrestAIStudio ?? {}, api);

  /* A review-only stand-in, so the generating / generated / compare states can
     be exercised without a backend. Gated behind an explicit query flag, the
     same convention the PDP pilot preview uses (?hc_pilot=1), and it labels
     every frame it produces so a demo result can never be mistaken for a real
     one. It returns the customer's OWN image back - it fabricates nothing. */
  function maybeEnableDemo() {
    let flagged = false;
    try {
      flagged = new URLSearchParams(window.location.search).get('hc_ai_demo') === '1';
    } catch {
      flagged = false;
    }
    if (!flagged || api.hasGenerator()) return false;
    api.registerGenerator(
      ({ original }) =>
        new Promise((resolve, reject) => {
          setTimeout(() => {
            const src = original?.url || original?.dataUrl;
            if (!src) reject(new Error('No source image.'));
            else resolve({ url: src, demo: true });
          }, 1400);
        })
    );
    return true;
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('Could not read that file.'));
      reader.readAsDataURL(file);
    });
  }

  /* --- ids ---------------------------------------------------------------- */

  function randomId() {
    try {
      if (crypto?.randomUUID) return crypto.randomUUID().replace(/-/g, '');
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return `hc${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    }
  }

  /* One session id per browser tab. Held in sessionStorage so a reload does not
     hand the visitor a fresh budget - the Worker's per-session allowance is a
     cost control and resetting it on F5 would defeat it. */
  function sessionIdentifier() {
    const KEY = 'hc_ai_session';
    try {
      const found = sessionStorage.getItem(KEY);
      if (found && ID_RE.test(found)) return found;
      const made = randomId();
      sessionStorage.setItem(KEY, made);
      return made;
    } catch {
      return randomId();
    }
  }

  /* --- errors ------------------------------------------------------------- */

  /* `verify` is the one kind the customer can act on differently: reload the
     page. Everything else is "try again", so the distinction is carried rather
     than flattened into a string comparison later. */
  class StudioError extends Error {
    constructor(message, kind) {
      super(message);
      this.name = 'StudioError';
      this.kind = kind || 'generic';
    }
  }

  /* --- Turnstile ---------------------------------------------------------- */

  /* Loaded on demand, not on page load: a visitor who never opens the studio
     should not fetch Cloudflare's script at all. One load is shared by every
     studio on the page. */
  let turnstileScript = null;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileScript) return turnstileScript;
    turnstileScript = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('turnstile unavailable'));
      };
      script.onerror = () => {
        turnstileScript = null;
        reject(new Error('turnstile blocked'));
      };
      document.head.appendChild(script);
    });
    return turnstileScript;
  }

  /*
   * A fresh token per request.
   *
   * Cloudflare treats a token as single-use, and the Worker deliberately does
   * not send an idempotency_key so that it stays that way. Reusing one across
   * an isolate and an edit would come back `timeout-or-duplicate` and read as
   * a verification failure, so every call executes the widget again.
   *
   * `interaction-only` keeps the widget invisible unless Cloudflare actually
   * wants an interaction, which is why wiring this in changes nothing on
   * screen in the ordinary case.
   */
  function createVerifier(mount, sitekey) {
    let widgetId = null;
    let pending = null;

    const settle = (fn, value) => {
      const p = pending;
      pending = null;
      if (p) p[fn](value);
    };

    async function token() {
      if (!sitekey) throw new StudioError('verification is not configured', 'verify');
      if (!mount) throw new StudioError('verification is not available', 'verify');

      let ts;
      try {
        ts = await loadTurnstile();
      } catch {
        throw new StudioError('verification could not load', 'verify');
      }

      if (pending) throw new StudioError('a verification is already running', 'verify');

      if (widgetId === null || widgetId === undefined) {
        try {
          widgetId = ts.render(mount, {
            sitekey,
            execution: 'execute',
            appearance: 'interaction-only',
            retry: 'never',
            callback: (value) => settle('resolve', value),
            'error-callback': () => {
              settle('reject', new StudioError('verification failed', 'verify'));
              return true;
            },
            'expired-callback': () =>
              settle('reject', new StudioError('verification expired', 'verify')),
            'timeout-callback': () =>
              settle('reject', new StudioError('verification timed out', 'verify')),
          });
        } catch {
          throw new StudioError('verification could not start', 'verify');
        }
        if (widgetId === null || widgetId === undefined) {
          throw new StudioError('verification could not start', 'verify');
        }
      } else {
        try {
          ts.reset(widgetId);
        } catch {
          /* A widget that refuses to reset still executes; the paths below
             cover the case where it then fails to produce a token. */
        }
      }

      return new Promise((resolve, reject) => {
        const timer = setTimeout(
          () => settle('reject', new StudioError('verification timed out', 'verify')),
          TURNSTILE_TIMEOUT_MS
        );
        pending = {
          resolve: (value) => {
            clearTimeout(timer);
            if (value) resolve(value);
            else reject(new StudioError('verification returned nothing', 'verify'));
          },
          reject: (error) => {
            clearTimeout(timer);
            reject(error);
          },
        };
        try {
          ts.execute(widgetId);
        } catch {
          settle('reject', new StudioError('verification could not run', 'verify'));
        }
      });
    }

    return { token };
  }

  function init(root) {
    if (!root || root.dataset.hcAiBound === 'true') return;
    const studio = root.querySelector('[data-hc-ai-studio]');
    if (!studio) return;
    root.dataset.hcAiBound = 'true';

    const demoMode = maybeEnableDemo();

    const el = {
      stage: studio.querySelector('[data-hc-ai-stage]'),
      imgOriginal: studio.querySelector('[data-hc-ai-img="original"]'),
      imgGenerated: studio.querySelector('[data-hc-ai-img="generated"]'),
      compare: studio.querySelector('[data-hc-ai-compare]'),
      compareAfter: studio.querySelector('[data-hc-ai-compare-after]'),
      compareBefore: studio.querySelector('[data-hc-ai-compare-before]'),
      compareAfterImg: studio.querySelector('[data-hc-ai-compare-after-img]'),
      compareRange: studio.querySelector('[data-hc-ai-compare-range]'),
      compareHandle: studio.querySelector('[data-hc-ai-compare-handle]'),
      states: Object.fromEntries(
        STAGE_STATES.map((s) => [s, studio.querySelector(`[data-hc-ai-state="${s}"]`)])
      ),
      errorText: studio.querySelector('[data-hc-ai-error-text]'),
      segs: [...studio.querySelectorAll('[data-hc-ai-view]')],
      thumbs: [...studio.querySelectorAll('[data-hc-ai-thumb]')],
      thumbImgs: {
        original: studio.querySelector('[data-hc-ai-thumb-img="original"]'),
        generated: studio.querySelector('[data-hc-ai-thumb-img="generated"]'),
      },
      thumbEmpties: {
        original: studio.querySelector('[data-hc-ai-thumb-empty="original"]'),
        generated: studio.querySelector('[data-hc-ai-thumb-empty="generated"]'),
      },
      baBtn: studio.querySelector('[data-hc-ai-ba]'),
      baBefore: studio.querySelector('[data-hc-ai-ba-label="before"]'),
      baAfter: studio.querySelector('[data-hc-ai-ba-label="after"]'),
      fullscreen: studio.querySelector('[data-hc-ai-fullscreen]'),
      download: studio.querySelector('[data-hc-ai-download]'),
      tabs: [...studio.querySelectorAll('[data-hc-ai-tab]')],
      prompt: studio.querySelector('[data-hc-ai-prompt]'),
      generate: studio.querySelector('[data-hc-ai-generate]'),
      chips: studio.querySelector('[data-hc-ai-chips]'),
      ideasLabel: studio.querySelector('[data-hc-ai-ideas-label]'),
      demoBadge: studio.querySelector('[data-hc-ai-demo-badge]'),
      turnstile: studio.querySelector('[data-hc-ai-turnstile]'),
      detect: studio.querySelector('[data-hc-ai-detect]'),
      detectImg: studio.querySelector('[data-hc-ai-detect-img]'),
      detectFrame: studio.querySelector('[data-hc-ai-detect-frame]'),
      regions: studio.querySelector('[data-hc-ai-regions]'),
      selectCount: studio.querySelector('[data-hc-ai-select-count]'),
      selectChosen: studio.querySelector('[data-hc-ai-select-chosen]'),
      selectConfirm: studio.querySelector('[data-hc-ai-select-confirm]'),
      file: root.querySelector('[data-hc-file]'),
    };

    /* ---------------------------------------------------------------- config */
    /* Endpoint and sitekey come from the section settings. An endpoint that is
       not an https URL is treated as absent rather than trusted, so a typo in
       the theme editor degrades to the offline viewer instead of posting the
       customer's photograph somewhere unintended. */
    const config = (() => {
      const raw = (studio.dataset.hcAiEndpoint || '').trim().replace(/\/+$/, '');
      let endpoint = '';
      try {
        if (raw && new URL(raw).protocol === 'https:') endpoint = raw;
      } catch {
        endpoint = '';
      }
      return {
        endpoint,
        sitekey: (studio.dataset.hcAiSitekey || '').trim(),
        verifyMessage:
          (studio.dataset.hcAiVerifyMessage || '').trim() ||
          'We could not verify your browser. Please reload the page and try again.',
      };
    })();

    const connected = Boolean(config.endpoint);
    const verifier = createVerifier(el.turnstile, config.sitekey);
    const sessionId = sessionIdentifier();

    /* ---------------------------------------------------------------- state */
    const state = {
      originalImage: null, // { url, objectKey, name, file }
      generatedImage: null, // { url, prompt, demo }
      activeImage: 'original', // which one the main stage is showing
      comparisonMode: false,
      generationStatus: 'empty', // empty | detecting | selecting | isolating | ready | generating | generated | error | unavailable
      message: '',
      /* The uploaded room photo and what was found in it. Held for the life of
         the session so a second selection does not need a second upload - and
         deliberately separate from originalImage, which only ever holds the
         isolated piece. */
      source: null, // { file, previewUrl, fingerprint, items }
      selected: null, // the chosen item
      /* The photo the customer just chose, shown in the stage straight away so
         the empty state gives way to their own image without waiting on the
         network. VIEW ONLY: it is never promoted to originalImage, which still
         only ever holds the isolated piece the AI flow derives from. */
      uploadPreviewUrl: null,
    };

    /* Increments on every file choice. A response whose ticket is stale belongs
       to a photo the customer has already replaced, so it is dropped rather
       than allowed to overwrite the newer one. */
    let uploadTicket = 0;

    let chipSets = {};
    try {
      chipSets = JSON.parse(studio.dataset.hcAiChipsets || '{}');
    } catch {
      chipSets = {};
    }

    /* ---------------------------------------------------------------- render */
    function show(node, on) {
      if (node) node.hidden = !on;
    }

    function originalSrc() {
      return state.originalImage?.url || state.uploadPreviewUrl || '';
    }

    function render() {
      const hasOriginal = Boolean(state.originalImage);
      const hasGenerated = Boolean(state.generatedImage);
      /* Either the isolated piece or, before one exists, the customer's own
         upload. Both are shown by the same layer. */
      const hasStageImage = hasOriginal || Boolean(state.uploadPreviewUrl);
      const isolating = state.generationStatus === 'isolating';
      const detecting = state.generationStatus === 'detecting';
      const selecting = state.generationStatus === 'selecting';
      const busy = state.generationStatus === 'generating' || isolating || detecting;

      // Stage layers. Exactly one of the overlay states is ever visible.
      STAGE_STATES.forEach((s) => show(el.states[s], false));

      const showingCompare = state.comparisonMode && hasOriginal && hasGenerated && !busy;
      const activeIsGenerated = state.activeImage === 'generated' && hasGenerated;
      /* Before an Original exists the stage is showing the customer's upload.
         That one stays put while the flow reports progress over it - otherwise
         the status overlay replaces it in the very tick it was set and the
         photo is never painted at all. */
      const previewOnly = !hasOriginal && Boolean(state.uploadPreviewUrl);

      show(el.compare, showingCompare && !selecting);
      show(
        el.imgOriginal,
        !showingCompare && hasStageImage && !activeIsGenerated && (!busy || previewOnly) && !selecting
      );
      show(el.imgGenerated, !showingCompare && activeIsGenerated && !busy && !selecting);

      /* The selection overlay sits over the UPLOADED photo, which is shown here
         and here only. It never becomes the Original. */
      show(el.detect, selecting);

      if (detecting) show(el.states.detecting, true);
      else if (selecting) show(el.states.selecting, true);
      else if (isolating) show(el.states.isolating, true);
      else if (busy) show(el.states.generating, true);
      else if (state.generationStatus === 'error') show(el.states.error, true);
      else if (state.generationStatus === 'unavailable') show(el.states.unavailable, true);
      else if (!hasStageImage) show(el.states.empty, true);

      if (el.errorText) el.errorText.textContent = state.message || '';

      // Images
      if (hasStageImage) {
        if (el.imgOriginal.getAttribute('src') !== originalSrc()) {
          el.imgOriginal.src = originalSrc();
        }
        // The thumbnail rail belongs to the Original/result pair, so it only
        // fills once a real Original exists.
        if (hasOriginal && el.thumbImgs.original) el.thumbImgs.original.src = originalSrc();
      }
      if (hasGenerated) {
        if (el.imgGenerated.getAttribute('src') !== state.generatedImage.url) {
          el.imgGenerated.src = state.generatedImage.url;
        }
        if (el.thumbImgs.generated) el.thumbImgs.generated.src = state.generatedImage.url;
      }
      // The comparison panes are their own <img> elements, so they need the
      // sources too - the single-image layers above are hidden while comparing.
      if (hasOriginal && el.compareBefore && el.compareBefore.getAttribute('src') !== originalSrc()) {
        el.compareBefore.src = originalSrc();
      }
      if (hasGenerated && el.compareAfterImg && el.compareAfterImg.getAttribute('src') !== state.generatedImage.url) {
        el.compareAfterImg.src = state.generatedImage.url;
      }

      show(el.thumbImgs.original, hasOriginal);
      show(el.thumbEmpties.original, !hasOriginal);
      show(el.thumbImgs.generated, hasGenerated);
      show(el.thumbEmpties.generated, !hasGenerated);

      // Segmented control
      el.segs.forEach((b) => {
        const isCompare = b.dataset.hcAiView === 'compare';
        b.setAttribute('aria-selected', String(isCompare ? state.comparisonMode : !state.comparisonMode));
        if (isCompare) b.disabled = !(hasOriginal && hasGenerated);
      });

      // Thumbnails
      el.thumbs.forEach((b) => {
        const which = b.dataset.hcAiThumb;
        b.setAttribute('aria-pressed', String(!state.comparisonMode && state.activeImage === which));
        b.disabled = which === 'original' ? !hasOriginal : !hasGenerated;
      });

      // Before / after
      if (el.baBtn) {
        el.baBtn.disabled = !(hasOriginal && hasGenerated);
        el.baBtn.dataset.showing = activeIsGenerated ? 'generated' : 'original';
        el.baBtn.setAttribute(
          'aria-label',
          activeIsGenerated ? 'Show the original photo' : 'Show the AI result'
        );
      }
      if (el.baBefore) el.baBefore.dataset.active = String(!activeIsGenerated);
      if (el.baAfter) el.baAfter.dataset.active = String(activeIsGenerated);

      // Tools
      if (el.fullscreen) el.fullscreen.disabled = !hasOriginal;
      if (el.download) {
        const src = activeIsGenerated ? state.generatedImage?.url : originalSrc();
        el.download.disabled = !src;
        if (src) {
          el.download.href = src;
          el.download.setAttribute(
            'download',
            activeIsGenerated ? 'homecrest-ai-result.png' : state.originalImage?.name || 'original.png'
          );
        } else {
          el.download.removeAttribute('href');
        }
      }

      // Generate button
      if (el.generate) {
        el.generate.disabled = busy;
        el.generate.dataset.state = state.generationStatus;
      }

      if (el.demoBadge) show(el.demoBadge, Boolean(state.generatedImage?.demo));

      studio.dataset.hcAiStatus = state.generationStatus;
    }

    /* ------------------------------------------------------------- selection */

    /*
     * Draws one button per detected item, positioned in PERCENTAGES.
     *
     * No pixel arithmetic anywhere: the frame shrink-wraps the photo, so a box
     * at 34% of the width sits over the same furniture whether the stage is
     * 1200px wide on a desktop or 340px on a phone, and it stays correct
     * through resize and rotation without a listener.
     */
    function renderRegions() {
      if (!el.regions) return;
      el.regions.textContent = '';
      const items = state.source?.items ?? [];

      items.forEach((item) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hc-ai__region';
        btn.style.left = `${item.box.x * 100}%`;
        btn.style.top = `${item.box.y * 100}%`;
        btn.style.width = `${item.box.width * 100}%`;
        btn.style.height = `${item.box.height * 100}%`;
        btn.dataset.hcAiRegion = item.id;
        btn.setAttribute('aria-pressed', String(state.selected?.id === item.id));
        btn.setAttribute('aria-label', `Select the ${item.label}`);

        const tag = document.createElement('span');
        tag.className = 'hc-ai__region-tag';
        tag.textContent = item.label;
        btn.appendChild(tag);

        btn.addEventListener('click', () => selectItem(item));
        el.regions.appendChild(btn);
      });

      if (el.selectCount) {
        const n = items.length;
        el.selectCount.textContent = n === 1
          ? 'We found 1 piece of furniture.'
          : `We found ${n} pieces of furniture.`;
      }
      if (el.selectChosen) {
        el.selectChosen.textContent = state.selected ? `${state.selected.label} selected` : '';
        show(el.selectChosen, Boolean(state.selected));
      }
      if (el.selectConfirm) el.selectConfirm.disabled = !state.selected;
    }

    function selectItem(item) {
      state.selected = item;
      renderRegions();
      [...(el.regions?.children ?? [])].forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.hcAiRegion === item.id));
      });
    }

    /* ---------------------------------------------------------------- actions */
    /* Local, synchronous and free: an object URL off the File the customer
       already picked. Nothing is uploaded for the preview's sake. */
    function setUploadPreview(file) {
      if (state.uploadPreviewUrl) URL.revokeObjectURL(state.uploadPreviewUrl);
      state.uploadPreviewUrl = file ? URL.createObjectURL(file) : null;
    }

    function setOriginal(image) {
      state.originalImage = image;
      // A new source photo invalidates a result generated from the old one.
      state.generatedImage = null;
      state.activeImage = 'original';
      state.comparisonMode = false;
      state.generationStatus = 'ready';
      state.message = '';
      render();
    }

    function clearOriginal() {
      if (state.source?.previewUrl) URL.revokeObjectURL(state.source.previewUrl);
      setUploadPreview(null);
      state.source = null;
      state.selected = null;
      if (el.regions) el.regions.textContent = '';
      state.originalImage = null;
      state.generatedImage = null;
      state.activeImage = 'original';
      state.comparisonMode = false;
      state.generationStatus = 'empty';
      state.message = '';
      render();
    }

    function fail(error) {
      state.generationStatus = 'error';
      state.message = customerMessage(error);
      render();
    }

    /* One sentence, always customer-facing. The Worker's own `error` strings are
       written for a customer and carry no internals, so they are preferred when
       present; nothing else about a response is ever surfaced. */
    function customerMessage(error) {
      if (error?.kind === 'verify' || error?.status === 403) return config.verifyMessage;
      if (error?.kind === 'network') {
        return 'We could not reach the visualiser. Check your connection and try again.';
      }
      if (error?.customerError) return error.customerError;
      return error?.message || 'That did not work. Please try again.';
    }

    /* --- transport ------------------------------------------------------- */

    async function send(path, init, { expect = 'image' } = {}) {
      let response;
      try {
        response = await fetch(`${config.endpoint}${path}`, { ...init, mode: 'cors' });
      } catch {
        throw new StudioError('network', 'network');
      }

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok || !payload?.ok) {
        const error = new StudioError(
          payload?.error || 'request failed',
          response.status === 403 ? 'verify' : 'generic'
        );
        error.status = response.status;
        /* The Worker writes these for a customer. Anything it did not write is
           replaced by our own sentence rather than shown raw. */
        error.customerError = typeof payload?.error === 'string' ? payload.error : '';
        throw error;
      }

      if (expect === 'items') {
        if (!Array.isArray(payload.items) || typeof payload.image_fingerprint !== 'string') {
          throw new StudioError('We could not read that photo. Please try another image.', 'generic');
        }
        return payload;
      }

      if (!payload.image_url || !payload.object_key) {
        throw new StudioError('The visualiser did not return an image.', 'generic');
      }
      return payload;
    }

    async function requestDetection(file) {
      const body = new FormData();
      body.append('image', file, file.name || 'photo.jpg');
      body.append('session_id', sessionId);
      body.append('generation_id', randomId());
      body.append('turnstile_token', await verifier.token());
      return send('/detect', { method: 'POST', body }, { expect: 'items' });
    }

    async function requestIsolation(file, selection) {
      const body = new FormData();
      body.append('image', file, file.name || 'photo.jpg');
      body.append('session_id', sessionId);
      body.append('generation_id', randomId());
      body.append('turnstile_token', await verifier.token());

      /* The chosen piece. Sent only when the customer actually chose - an
         upload with nothing to choose between still uses the original
         single-product request exactly as before. */
      if (selection) {
        body.append('item_id', selection.item.id);
        body.append('item_label', selection.item.label);
        body.append('box_x', String(selection.item.box.x));
        body.append('box_y', String(selection.item.box.y));
        body.append('box_w', String(selection.item.box.width));
        body.append('box_h', String(selection.item.box.height));
        body.append('image_fingerprint', selection.fingerprint);
      }
      // No content-type header: the browser must set the multipart boundary.
      return send('/isolate', { method: 'POST', body });
    }

    async function requestEdit(instruction) {
      const body = {
        /* ALWAYS the Original's key. An edit derived from the previous result
           would compound the model's drift; deriving every edit from the same
           isolated object is what keeps Original meaningful. */
        object_key: state.originalImage.objectKey,
        instruction,
        session_id: sessionId,
        generation_id: randomId(),
        turnstile_token: await verifier.token(),
        context: collectContext(),
      };
      return send('/edit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
    }

    /* --- the two journeys ------------------------------------------------ */

    /* The room photo is posted once and never shown. What comes back - the
       piece alone on white - becomes the Original, and it is the only thing
       any later edit is derived from. */
    /*
     * Upload -> detect -> (choose) -> isolate.
     *
     * Detection runs first so the customer can say WHICH piece they mean. The
     * room photo is shown during selection and then set aside: only the
     * isolated piece ever becomes the Original.
     */
    async function detect(file) {
      const ticket = ++uploadTicket;

      if (state.source?.previewUrl) URL.revokeObjectURL(state.source.previewUrl);
      state.source = null;
      state.selected = null;
      state.originalImage = null;
      state.generatedImage = null;
      state.activeImage = 'original';
      state.comparisonMode = false;
      state.generationStatus = 'detecting';
      state.message = '';
      render();

      let result;
      try {
        result = await requestDetection(file);
      } catch (error) {
        if (ticket !== uploadTicket) return;
        fail(error);
        return;
      }
      if (ticket !== uploadTicket) return; // a newer photo won

      if (!result.items.length) {
        state.generationStatus = 'error';
        state.message = 'We could not identify a furniture piece in this photo. Please try another image.';
        render();
        return;
      }

      state.source = {
        file,
        previewUrl: URL.createObjectURL(file),
        fingerprint: result.image_fingerprint,
        items: result.items,
      };
      if (el.detectImg) {
        /* Hand the photo's own aspect ratio to CSS so the overlay letterboxes
           exactly as the image does. This is the only geometry JavaScript is
           involved in, it is read once from the decoded image, and nothing
           recalculates on resize - the ratio does not change. */
        el.detectImg.onload = () => {
          const w = el.detectImg.naturalWidth;
          const h = el.detectImg.naturalHeight;
          if (!w || !h || !el.stage) return;
          /* Two forms of the same fact, because CSS needs both: the ratio for
             aspect-ratio, and a plain number for the width cap's calc(). Set on
             the STAGE, which is the element that reads them - a custom property
             inherits downwards, so putting it on the frame left the stage
             unable to see it. */
          el.stage.style.setProperty('--hc-detect-ratio', `${w} / ${h}`);
          el.stage.style.setProperty('--hc-detect-ar', String(w / h));
        };
        el.detectImg.src = state.source.previewUrl;
      }

      /* One piece is not a choice. Select it and move on rather than making the
         customer confirm what they already implied by uploading it. */
      if (result.items.length === 1) {
        state.selected = result.items[0];
        renderRegions();
        isolate(file, { item: state.selected, fingerprint: state.source.fingerprint });
        return;
      }

      state.selected = null;
      state.generationStatus = 'selecting';
      renderRegions();
      render();
    }

    async function isolate(file, selection = null) {
      const ticket = ++uploadTicket;

      state.originalImage = null;
      state.generatedImage = null;
      state.activeImage = 'original';
      state.comparisonMode = false;
      state.generationStatus = 'isolating';
      state.message = '';
      render();

      try {
        const result = await requestIsolation(file, selection);
        if (ticket !== uploadTicket) return; // a newer photo won
        setOriginal({
          url: result.image_url,
          objectKey: result.object_key,
          name: selection ? `${selection.item.label}` : file.name || 'original',
          file,
        });
      } catch (error) {
        if (ticket !== uploadTicket) return;
        fail(error);
      }
    }

    async function generate() {
      if (state.generationStatus === 'isolating') {
        state.message = 'One moment - we are still preparing your photo.';
        render();
        return;
      }
      if (!state.originalImage) {
        state.generationStatus = 'error';
        state.message = 'Upload a photo of the piece first, under "Your inspiration".';
        render();
        return;
      }
      const prompt = (el.prompt?.value || '').trim();
      if (!prompt) {
        state.generationStatus = 'error';
        state.message = 'Describe the change you would like to see.';
        render();
        el.prompt?.focus();
        return;
      }
      if (prompt.length > MAX_INSTRUCTION_CHARS) {
        /* Refused here rather than silently shortened server-side, so the
           customer is never shown a result answering half their sentence. */
        state.generationStatus = 'error';
        state.message = `Please shorten that to ${MAX_INSTRUCTION_CHARS} characters or fewer.`;
        render();
        el.prompt?.focus();
        return;
      }

      /* A registered generator still wins, so ?hc_ai_demo=1 and any externally
         supplied backend behave exactly as they did before this was wired. */
      const useWorker = !api.hasGenerator() && connected && Boolean(state.originalImage.objectKey);
      if (!api.hasGenerator() && !useWorker) {
        // No backend. Say so rather than staging a result that never happened.
        state.generationStatus = 'unavailable';
        state.message = '';
        render();
        return;
      }

      state.generationStatus = 'generating';
      state.message = '';
      render();

      try {
        let url;
        let demo = false;
        if (useWorker) {
          const result = await requestEdit(prompt);
          url = result.image_url;
        } else {
          const result = await generator({
            prompt,
            original: {
              ...state.originalImage,
              // Long-standing field name in the registerGenerator contract.
              dataUrl: originalSrc(),
            },
            context: collectContext(),
          });
          url = result?.url;
          demo = Boolean(result?.demo);
          if (!url) throw new StudioError('The visualiser did not return an image.', 'generic');
        }

        /* Stored ALONGSIDE the original, never over it. */
        state.generatedImage = { url, prompt, demo };
        state.activeImage = 'generated';
        state.comparisonMode = false;
        state.generationStatus = 'generated';
        render();
      } catch (error) {
        fail(error);
      }
    }

    /* Read-only snapshot of what the customer has already told the brief form,
       so a generator has the same context without a second set of inputs. */
    function collectContext() {
      const val = (sel) => root.querySelector(sel)?.value?.trim() || '';
      return {
        piece: val('select[name="contact[Designing]"]'),
        width: val('input[name="contact[Width]"]'),
        depth: val('input[name="contact[Depth]"]'),
        height: val('input[name="contact[Height]"]'),
        material: val('select[name="contact[Material]"]'),
        finish: val('select[name="contact[Finish]"]'),
      };
    }

    function appendToPrompt(text) {
      if (!el.prompt) return;
      const current = el.prompt.value.trim();
      el.prompt.value = current ? `${current.replace(/[.,;\s]+$/, '')}, ${text}` : text;
      el.prompt.focus();
      el.prompt.setSelectionRange(el.prompt.value.length, el.prompt.value.length);
      // A chip is an idea, not a command: nothing generates until Generate.
      if (state.generationStatus === 'error' || state.generationStatus === 'unavailable') {
        state.generationStatus = state.originalImage ? 'ready' : 'empty';
        state.message = '';
        render();
      }
    }

    function renderChips(tabKey) {
      if (!el.chips) return;
      const items = (Array.isArray(chipSets[tabKey]) ? chipSets[tabKey] : [])
        .map((label) => String(label).trim())
        .filter(Boolean);
      el.chips.textContent = '';
      items.forEach((label) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hc-ai__chip';
        btn.textContent = label;
        btn.addEventListener('click', () => appendToPrompt(label));
        li.appendChild(btn);
        el.chips.appendChild(li);
      });
      show(el.ideasLabel, items.length > 0);
      show(el.chips, items.length > 0);
    }

    function setTab(key) {
      el.tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.hcAiTab === key)));
      renderChips(key);
    }

    function setSplit(value) {
      const pct = Math.min(100, Math.max(0, Number(value) || 0));
      el.compare?.style.setProperty('--hc-split', `${pct}%`);
      if (el.compareAfter) el.compareAfter.style.clipPath = `inset(0 0 0 ${pct}%)`;
      if (el.compareHandle) el.compareHandle.style.left = `${pct}%`;
    }

    /* ---------------------------------------------------------------- events */
    // The studio listens to the brief form's OWN file input. There is no second
    // upload: the photo the customer attaches to the enquiry is the photo the
    // studio works from. The form's existing preview/filename handler is
    // untouched - that small thumbnail is the customer's receipt for what they
    // chose, which is a different thing from what the viewer shows.
    el.file?.addEventListener('change', async () => {
      const file = el.file.files && el.file.files[0];
      if (!file) {
        uploadTicket += 1; // cancels any isolation still in flight
        clearOriginal();
        return;
      }
      if (!file.type.startsWith('image/')) {
        setUploadPreview(null);
        state.generationStatus = 'error';
        state.message = 'That file is not an image. Use a JPG, PNG or WEBP.';
        render();
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setUploadPreview(null);
        state.generationStatus = 'error';
        state.message = 'That image is too large. Please use one under 8 MB.';
        render();
        return;
      }

      /* Show it now. Whatever the AI flow does next, the customer's own photo
         has already replaced the empty state. */
      setUploadPreview(file);
      render();

      /* CONNECTED: the room photo goes to /isolate and never to the viewer.
         OFFLINE: there is nothing to isolate it with, so a local preview is all
         the studio can honestly show. */
      if (connected) {
        detect(file);
        return;
      }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        uploadTicket += 1;
        setOriginal({ url: dataUrl, objectKey: null, name: file.name || 'original', file });
      } catch (error) {
        state.generationStatus = 'error';
        state.message = error?.message || 'That image could not be read.';
        render();
      }
    });

    /* Upload from the preview side. Both entry points share the Step 01 input,
       so choosing a file here fires exactly the same change event the form's
       own handlers already listen to - the card, the thumbnail, the hidden
       filename field and this studio all update from that one event, and there
       is only ever one selected file and one object URL. */
    const emptyState = el.states.empty;
    if (emptyState && el.file) {
      emptyState.addEventListener('click', () => el.file.click());

      const setDrag = (on) => emptyState.classList.toggle('is-dragover', on);

      emptyState.addEventListener('dragover', (e) => {
        e.preventDefault();
        setDrag(true);
      });
      emptyState.addEventListener('dragleave', () => setDrag(false));
      emptyState.addEventListener('drop', (e) => {
        e.preventDefault();
        setDrag(false);
        const dropped = e.dataTransfer?.files?.[0];
        if (!dropped) return;

        /* `accept` filters the file picker but says nothing about a drop, so
           the same restriction is applied here. Without it a drop is a way to
           put a file into the form that the picker would have refused. */
        const accept = (el.file.accept || '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);
        if (accept.length && !accept.includes(dropped.type)) {
          state.generationStatus = 'error';
          state.message = 'That file is not an image. Use a JPG, PNG or WEBP.';
          render();
          return;
        }
        /* Hand the file to the real input so the existing change handler does
           the validating - a drop is not a way around it. */
        const dt = new DataTransfer();
        dt.items.add(dropped);
        el.file.files = dt.files;
        el.file.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    el.segs.forEach((btn) =>
      btn.addEventListener('click', () => {
        state.comparisonMode = btn.dataset.hcAiView === 'compare';
        if (state.comparisonMode) setSplit(el.compareRange?.value ?? 50);
        render();
      })
    );

    el.thumbs.forEach((btn) =>
      btn.addEventListener('click', () => {
        state.activeImage = btn.dataset.hcAiThumb;
        state.comparisonMode = false;
        render();
      })
    );

    el.baBtn?.addEventListener('click', () => {
      state.activeImage = state.activeImage === 'generated' ? 'original' : 'generated';
      state.comparisonMode = false;
      render();
    });

    el.selectConfirm?.addEventListener('click', () => {
      if (!state.selected || !state.source) return;
      isolate(state.source.file, { item: state.selected, fingerprint: state.source.fingerprint });
    });

    el.compareRange?.addEventListener('input', (e) => setSplit(e.target.value));

    el.fullscreen?.addEventListener('click', () => {
      const node = el.stage;
      if (!node) return;
      if (document.fullscreenElement) document.exitFullscreen?.();
      else node.requestFullscreen?.().catch(() => {});
    });

    el.tabs.forEach((tab) => tab.addEventListener('click', () => setTab(tab.dataset.hcAiTab)));

    el.generate?.addEventListener('click', (event) => {
      // Belt and braces. The studio sits outside <form> so this button cannot
      // submit the enquiry, and the explicit guard keeps that true if the
      // markup is ever moved inside it.
      event.preventDefault();
      event.stopPropagation();
      generate();
    });

    // Cmd/Ctrl+Enter from the prompt is a generate, never a form submit.
    el.prompt?.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        generate();
      }
    });

    if (demoMode && el.prompt) {
      el.prompt.setAttribute('data-hc-ai-demo-active', 'true');
    }

    setTab(el.tabs[0]?.dataset.hcAiTab || 'change');
    setSplit(50);
    render();
  }

  function boot() {
    document.querySelectorAll('[data-hc-ai-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor re-renders the section wholesale.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('[data-hc-ai-root]');
    if (root) init(root);
  });
})();
